import { type JSX, useMemo } from 'react';
import { SurveyQuestionType } from '@trinity/chat/enums';
import type { SurveyAggregate } from '@trinity/chat/types/survey';
import { Spinner } from '@trinity/chat-components/components/presentation/Spinner.js';
import { ConjointAnalysisSurveyResponsesContainer } from '@/containers/SurveyResponses/ConjointAnalysisContainer';
import { useSurveyQuestionResponsesData } from '@/containers/SurveyResponses/hooks/useSurveyQuestionResponsesData';
import { MaxDiffSurveyResponsesContainer } from '@/containers/SurveyResponses/MaxDiffContainer';
import ConjointAnalysisResponses from './ConjointAnalysis';
import { ImageMarkupResponses } from './ImageMarkup';
import MatrixGridResponses from './MatrixGrid';
import MatrixMultiselectResponses from './MatrixMultiselect';
import { MatrixRangeResponses } from './MatrixRange';
import MatrixSliderResponses from './MatrixSlider';
import MaxDiffResponses from './MaxDiff';
import MultiTextboxes from './MultiTextboxes';
import NumberInputTableResponses from './NumberInputTable';
import ResponseRanking from './Ranking';
import { ResponseExclusiveOptions } from './ResponseExclusiveOptions';
import ResponseOptions from './ResponseOptions';
import { ResponsesEmpty, NotEnoughResponses } from './ResponsesEmpty';
import ResponseText from './ResponseText';
import styles from './style/QuestionResponses.module.css';

type ConditionalProps = {
  hasResponses: boolean;
  emptyElement?: () => JSX.Element;
} & ChildrenProps;

const $HasResponses = (props: ConditionalProps) => {
  if (props.hasResponses) {
    return <>{props.children}</>;
  } else {
    if (props.emptyElement) {
      return <props.emptyElement />;
    } else {
      return <ResponsesEmpty />;
    }
  }
};

const QuestionResponses = () => {
  const { question, rationale, responses, total } = useSurveyQuestionResponsesData();

  const hasResponses = useMemo(() => {
    if (!question) {
      return false;
    }

    if (question.typeId === SurveyQuestionType.ConjointAnalysis) {
      return total > 5;
    }

    return total > 0;
  }, [
    question,
    total,
  ]);

  if (!responses) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  const typeId = question.typeId;

  switch (typeId) {
    case SurveyQuestionType.Dropdown:
    case SurveyQuestionType.Multiselect:
    case SurveyQuestionType.MultipleChoice: {
      assertResponses<SurveyQuestionType.Multiselect | SurveyQuestionType.MultipleChoice>(responses);

      return (
        <$HasResponses hasResponses={hasResponses}>
          <ResponseOptions
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.Ranking: {
      assertResponses<SurveyQuestionType.Ranking>(responses);

      return (
        <$HasResponses hasResponses={hasResponses}>
          <ResponseRanking
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.LongTextResponse:
    case SurveyQuestionType.ShortTextResponse: {
      assertResponses<SurveyQuestionType.LongTextResponse | SurveyQuestionType.ShortTextResponse>(responses);

      return (
        <$HasResponses hasResponses={hasResponses}>
          <ResponseText
            key={question.id}
            question={question}
            rationale={rationale}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.Sliders: {
      assertResponses<SurveyQuestionType.Sliders>(responses);
      return (
        <$HasResponses hasResponses={hasResponses}>
          <MatrixSliderResponses
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.MatrixGrid: {
      assertResponses<SurveyQuestionType.MatrixGrid>(responses);
      return (
        <$HasResponses hasResponses={hasResponses}>
          <MatrixGridResponses
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.MatrixMultiselect: {
      assertResponses<SurveyQuestionType.MatrixMultiselect>(responses);
      return (
        <$HasResponses hasResponses={hasResponses}>
          <MatrixMultiselectResponses
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.ConjointAnalysis: {
      assertResponses<SurveyQuestionType.ConjointAnalysis>(responses);

      return (
        <$HasResponses
          hasResponses={hasResponses}
          emptyElement={NotEnoughResponses}>
          <ConjointAnalysisSurveyResponsesContainer key={question.id}>
            <ConjointAnalysisResponses />
          </ConjointAnalysisSurveyResponsesContainer>
        </$HasResponses>
      );
    }

    case SurveyQuestionType.MaxDifference: {
      assertResponses<SurveyQuestionType.MaxDifference>(responses);

      return (
        <$HasResponses
          hasResponses={hasResponses}>
          <MaxDiffSurveyResponsesContainer key={question.id}>
            <MaxDiffResponses />
          </MaxDiffSurveyResponsesContainer>
        </$HasResponses>
      );
    }

    case SurveyQuestionType.NumberInputTable: {
      assertResponses<SurveyQuestionType.NumberInputTable>(responses);
      return (
        <$HasResponses
          hasResponses={hasResponses}>
          <NumberInputTableResponses
            key={question.id} />
        </$HasResponses>
      );
    }
    case SurveyQuestionType.MultiTextbox: {
      assertResponses<SurveyQuestionType.MultiTextbox>(responses);
      return (
        <$HasResponses
          hasResponses={hasResponses}>
          <MultiTextboxes
            question={question}
            responses={responses}
            key={question.id} />
        </$HasResponses>
      );
    }
    case SurveyQuestionType.ExclusiveOptions: {
      assertResponses<SurveyQuestionType.ExclusiveOptions>(responses);
      return (
        <$HasResponses hasResponses={hasResponses}>
          <ResponseExclusiveOptions
            key={question.id}
            question={question}
            responses={responses} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.MatrixRange: {
      return (
        <$HasResponses hasResponses={hasResponses}>
          <MatrixRangeResponses
            key={question.id} />
        </$HasResponses>
      );
    }

    case SurveyQuestionType.ImageMarkup: {
      assertResponses<SurveyQuestionType.ImageMarkup>(responses);
      return (
        <$HasResponses hasResponses={hasResponses}>
          <ImageMarkupResponses
            key={question.id}
            question={question}
            aggregateData={responses} />
        </$HasResponses>
      );
    }

    default: {
      // const error = new UnreachableCaseError(typeId);
      //Don't throw this error to maintain existing non-breaking behavior
      return null;
    }
  }
};

 
function assertResponses<T extends SurveyQuestionType = SurveyQuestionType>(data: SurveyAggregate.QuestionData): asserts data is SurveyAggregate.QuestionData<T> { }

export { QuestionResponses };
export default QuestionResponses;